<template>
  <div
    class="package-card"
    @click="click"
    :class="{ 'package-card_selected': selected, 'package-card_sold-out': soldOut, 'package-card_readonly': readonly }"
  >
    <div class="package-card__row">
      <div class="package-card__radio">
        <Radio :checked="selected" :disabled="soldOut || readonly" />
      </div>
      <div class="package-card__right">
        <div class="package-card__body">
          <div class="package-card__body-start">
            <div class="package-card__title">{{ packageName }} ({{ dateRange }})</div>
            <div class="package-card__subtitle" v-if="available">
              {{ t('day', { count: numberOfDays }, numberOfDays) }} ·
              {{ t('night', { count: numberOfDays - 1 }, numberOfDays - 1) }}
            </div>
            <div class="package-card__price" v-if="available && event.checkoutTierPricing">
              <PackagePrice
                :currency="itinerary.baseCurrency"
                :price-cents="event.checkoutTierPricing.priceCents"
                :early-bird-pricing="event.earlyBirdPricing"
              />
            </div>
          </div>
          <div class="package-card__body-end">
            <Button v-if="selected" color="primary" color-filled class="package-card__button" @click="clickBook">{{
              draftPreview ? t('checkout_preview.preview_checkout') : t('book_now')
            }}</Button>
            <div
              v-else-if="showAvailability"
              class="package-card__spots-left"
              :class="{ 'package-card__spots-left_zero': availabilityCount === 0 }"
            >
              {{ soldOut ? t('sold_out') : t('spots_left', { count: availabilityCount }, availabilityCount) }}
            </div>
          </div>
        </div>
        <MediaGallery v-if="event.images.length > 0" :feature-images="galleryImages" layout="row" />
        <div class="package-card__footer" v-if="available && !readonly">
          <div class="package-card__toggle" @click="toggle">
            <span class="package-card__body-title">{{ t('whats_included') }}</span>
            <i class="icon" :class="{ 'icon-arrow-down-1': !expanded, 'icon-arrow-up-1': expanded }" />
          </div>
          <div class="package-card__expanded-content" v-if="expanded">
            <div class="package-card__expanded-section" v-if="includedInPriceHtml.length">
              <div class="package-card__section-title">
                {{ t('included') }}
              </div>
              <div class="package-card__section-item" v-for="(item, idx) in includedInPriceHtml" :key="idx">
                <i class="icon icon-check-circle-1" />
                <span v-html="item" />
              </div>
            </div>
            <div class="package-card__expanded-section" v-if="excludedInPriceHtml.length">
              <div class="package-card__section-title">
                {{ t('not_included') }}
              </div>
              <div class="package-card__section-item" v-for="(item, idx) in excludedInPriceHtml" :key="idx">
                <i class="icon icon-remove-circle" />
                <span v-html="item" />
              </div>
            </div>
          </div>
        </div>
        <Button
          v-if="selected"
          color="primary"
          color-filled
          class="package-card__button package-card__button_mobile"
          @click="clickBook"
          >{{ draftPreview ? t('checkout_preview.preview_checkout') : t('book_now') }}</Button
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import moment from 'moment-timezone'
import { Radio, Button } from '@/components/Theme/index.js'
import PackagePrice from './PackagePrice.vue'
import type { EarlyBirdPricing } from './types'
import { calcDaysCount, calcDateRange } from '@/utils/selectPackage.js'
import MediaGallery from '@/modules/MediaGallery/MediaGallery.vue'
import { submitForm } from '@/utils/formSubmit'
import { redirectTo } from '@/utils/functions'
import { useShared } from '@/helpers/vueHelpers.js'

interface PackageCardItinerary {
  baseCurrency: string
  includedInPriceHtml?: string[]
  excludedInPriceHtml?: string[]
}

interface PackageCardEvent {
  id: string | number
  name?: string | null
  startsAt: string | Date
  endsAt: string | Date
  timezone: string
  checkoutTierPricing: { priceCents: number } | null
  earlyBirdPricing?: EarlyBirdPricing | null
  images: Array<{ description?: string; [key: string]: unknown }>
  includedInPriceHtml?: string[]
  excludedInPriceHtml?: string[]
  availableSlotsCount: number | null
  availableSpotsCount: number | null
  meta: { link?: string | null; draftPreview?: boolean }
}

const {
  itinerary,
  event,
  selected = false,
  readonly = false,
  showAvailability = true,
} = defineProps<{
  itinerary: PackageCardItinerary
  event: PackageCardEvent
  selected?: boolean
  readonly?: boolean
  showAvailability?: boolean
}>()
const emit = defineEmits<{ click: [eventId: string | number] }>()
const { t } = useShared()
const expanded = ref(false)

const packageName = computed(() => {
  if (event.name) {
    return event.name
  }
  const startDate = moment.tz(event.startsAt, event.timezone).format('MMM D, YYYY')
  const endDate = moment.tz(event.endsAt, event.timezone).format('MMM D, YYYY')
  return `${startDate} - ${endDate}`
})

const numberOfDays = computed(() => calcDaysCount(event))
const dateRange = computed(() => calcDateRange(event))
const availabilityCount = computed(() => event.availableSlotsCount ?? 0)
const draftPreview = computed(() => event.meta.draftPreview === true)
const soldOut = computed(() => !draftPreview.value && !readonly && (event.availableSpotsCount ?? 0) <= 0) // Sold out only if no available spots
const available = computed(() => !soldOut.value)
const includedInPriceHtml = computed(() =>
  (event.includedInPriceHtml ?? []).concat(itinerary.includedInPriceHtml ?? []),
)
const excludedInPriceHtml = computed(() =>
  (event.excludedInPriceHtml ?? []).concat(itinerary.excludedInPriceHtml ?? []),
)
const galleryImages = computed(() => {
  return event.images.map(img => ({
    ...img,
    description: `${packageName.value} (${dateRange.value}): ${img.description}`,
  }))
})

function toggle() {
  if (readonly) return
  expanded.value = !expanded.value
  if (expanded.value) {
    emit('click', event.id)
  }
}
function click() {
  if (readonly || soldOut.value) return
  if (!draftPreview.value && (event.availableSlotsCount ?? 0) <= 0) return
  emit('click', event.id)
}
function clickBook() {
  if (readonly) return
  if (!event.meta.link) return
  if (draftPreview.value) {
    redirectTo(event.meta.link)
    return
  }
  submitForm({ action: event.meta.link })
}
</script>

<style lang="scss" scoped>
.package-card {
  border: 1px solid $gray-lighter;
  border-radius: $rounded-xs;
  padding: $spacing-3;
  display: grid;
  gap: $spacing-3;
  cursor: pointer;

  .icon-check-circle-1 {
    color: $brand-tertiary;
  }
  .icon-remove-circle {
    color: $brand-secondary;
  }
}
.package-card__row {
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: $spacing-3;
  align-items: center;
}
.package-card__right {
  display: grid;
  row-gap: $spacing-2;
  @media (min-width: $breakpoint-m) {
    row-gap: $spacing-3;
  }
}
.package-card__body {
  display: grid;
  column-gap: $spacing-6;
  row-gap: $spacing-2;
  align-items: start;
  @media (min-width: $breakpoint-m) {
    grid-template-columns: 18fr 4fr;
  }
}
.package-card__body-start {
  display: grid;
  row-gap: $spacing-2;
}
.package-card__body-end {
  display: grid;
  @media (min-width: $breakpoint-m) {
    justify-items: end;
  }
}
.package-card__price {
  color: $brand-primary;
  font-weight: $font-semibold;
  line-height: $leading-snug;
}
.package-card__toggle {
  font-weight: $font-medium;
  display: flex;
  gap: $spacing-2;
  align-items: center;
  color: $brand-tertiary;
  width: fit-content;
  .icon {
    font-size: $text-xs;
    position: relative;
    top: 1px;
  }
}
.package-card__section-title {
  font-weight: $font-semibold;
}
.package-card__section-item {
  display: flex;
  gap: $spacing-2;
}

.package-card__spots-left {
  color: $brand-tertiary;
  font-weight: $font-semibold;
  line-height: $leading-normal;

  @media (min-width: $breakpoint-m) {
    justify-self: right;
  }

  &_zero {
    color: $gray;
  }
}
.package-card__title {
  font-weight: $font-semibold;
  line-height: $leading-snug;
}
.package-card__subtitle {
  font-weight: $font-medium;
  line-height: $leading-snug;
}
.package-card__expanded-content {
  display: grid;
  row-gap: $spacing-3;
}
.package-card__expanded-section {
  display: grid;
  row-gap: $spacing-2;
}
.package-card_sold-out {
  background-color: $gray-light;
  .package-card__title {
    color: $gray;
  }
  .package-card__spots-left {
    color: $brand-secondary;
  }
}
.package-card_selected {
  background-color: $brand-tertiary-light;
}
.package-card_readonly {
  cursor: default;
}
.package-card__button:not(.package-card__button_mobile) {
  display: none;
  @media (min-width: $breakpoint-m) {
    display: unset;
  }
}
.package-card__button_mobile {
  @media (min-width: $breakpoint-m) {
    display: none;
  }
}
.package-card__footer {
  display: grid;
  row-gap: $spacing-3;
}
</style>

<i18n lang="yaml">
en:
  day: '{count} day | {count} days'
  night: '{count} night | {count} nights'
  whats_included: 'What’s included?'
  included: Included
  not_included: Not included
  book_now: Book now
  spots_left: 'All spots claimed | {count} spot left | {count} spots left'
  sold_out: Sold out
</i18n>
